import { Task, WeekDays } from '@griply/types';
import { HALF_HOUR } from '@griply/utils';
import { DataType } from 'shared/types/data-type';
import { getDueTaskPlanning } from 'shared/utils/get-task-planning';

import { DraggableEventExtendedProps } from './extended-props';

export type DraggableEventDataOptions = {
  tasks: Task[];
  weekStartsOn: WeekDays;
  includeDuration?: boolean;
  includeDueDateAsOriginDate?: boolean;
};

export type DraggableEventData = {
  id: string;
  title: string | null;
  extendedProps: DraggableEventExtendedProps;
  create: boolean;
  duration?: { ms: number };
};

export const getDraggableEventData = (
  el: HTMLElement,
  {
    tasks,
    weekStartsOn,
    includeDuration,
    includeDueDateAsOriginDate,
  }: DraggableEventDataOptions,
): DraggableEventData | undefined => {
  // Popover items carry the data attributes on the element itself; side-panel rows on a descendant.
  const dataEl = el.matches('[data-component-type]')
    ? el
    : el.querySelector('[data-component-type]');
  const id = dataEl?.getAttribute('data-id');

  if (!dataEl) {
    return undefined;
  }

  if (!id) {
    return undefined;
  }

  const title = dataEl.getAttribute('data-title');
  const componentType = dataEl.getAttribute('data-component-type') as DataType;
  const task = tasks.find((task) => task.id === id);

  const extendedProps: DraggableEventExtendedProps = { task, weekStartsOn };

  switch (componentType) {
    case DataType.Task:
      extendedProps.taskId = id;
      break;
    case DataType.Goal:
      extendedProps.goalId = id;
      break;
    case DataType.LifeArea:
      extendedProps.lifeAreaId = id;
      break;
  }

  // for allowing dragging of the task from the list onto a due date, we get the due task planning as origin date.
  if (includeDueDateAsOriginDate && task) {
    const duePlanning = getDueTaskPlanning(task, {
      date: new Date(),
      today: new Date(),
      weekStartsOn,
      uncompletedOnly: true,
    });
    extendedProps.originDate = duePlanning[duePlanning.length - 1]?.date;
  }

  const eventBase = { id, title, extendedProps, create: true };

  if (!includeDuration) {
    return eventBase;
  }

  const rawDuration = dataEl.getAttribute('data-duration');
  return {
    ...eventBase,
    duration: { ms: rawDuration ? parseInt(rawDuration) : HALF_HOUR },
  };
};
